import {
  isAllowedNavigationHost,
  isExternalAppLink,
} from "@config/allowed-navigation-hosts";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { isNativePlatform } from "@lib/platform";
import { navigateInApp } from "@utils/navigation/app-nav-bridge";

function viteEnv(): Record<string, string | undefined> {
  return {
    VITE_MARKETING_URL: import.meta.env.VITE_MARKETING_URL,
    VITE_APP_URL: import.meta.env.VITE_APP_URL,
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  };
}

function isSameAppOrigin(parsed: URL): boolean {
  if (typeof window === "undefined") return false;
  return parsed.origin === window.location.origin;
}

function shouldUseClientRouter(parsed: URL): boolean {
  if (!isSameAppOrigin(parsed)) return false;
  const pathname = parsed.pathname;
  if (!pathname.startsWith("/")) return false;
  return !/\.(svg|png|jpe?g|webp|gif|css|js|woff2?|ico|map)$/i.test(pathname);
}

/** True when the URL should stay in-app on native (not the system browser app). */
export function shouldOpenInApp(href: string): boolean {
  if (!isNativePlatform()) return false;

  const trimmed = href.trim();
  if (!trimmed) return false;
  if (/^(tel:|mailto:|sms:|geo:|whatsapp:)/i.test(trimmed)) return false;

  let parsed: URL;
  try {
    parsed = new URL(trimmed, window.location.href);
  } catch {
    return false;
  }

  if (isExternalAppLink(trimmed, parsed.hostname)) return false;
  if (!["http:", "https:"].includes(parsed.protocol)) return false;

  if (shouldUseClientRouter(parsed)) return true;
  return isAllowedNavigationHost(parsed.hostname, viteEnv());
}

async function openInAppBrowser(url: string): Promise<void> {
  if (!Capacitor.isPluginAvailable("Browser")) {
    window.location.assign(url);
    return;
  }
  await Browser.open({ url, presentationStyle: "fullscreen" });
}

/**
 * Dismiss the in-app browser sheet.
 *
 * A deep link that reopens the app leaves SFSafariViewController presented on
 * top of it, so the user lands back on the browser instead of the route the
 * link points at. Call this when a `komut://` URL arrives.
 */
export async function closeInAppBrowser(): Promise<void> {
  if (!Capacitor.isPluginAvailable("Browser")) return;
  try {
    await Browser.close();
  } catch {
    // No browser window is open, which is the common case on web and on cold
    // start. Nothing to close.
  }
}

/** Open a URL in-app on native; default browser behavior on web. */
export function openUrl(href: string): void {
  const trimmed = href.trim();
  if (!trimmed) return;

  if (/^(tel:|mailto:|sms:|geo:|whatsapp:)/i.test(trimmed)) {
    window.location.assign(trimmed);
    return;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed, window.location.href);
  } catch {
    return;
  }

  if (isExternalAppLink(trimmed, parsed.hostname)) {
    window.location.assign(parsed.href);
    return;
  }

  if (isNativePlatform() && shouldOpenInApp(parsed.href)) {
    if (shouldUseClientRouter(parsed)) {
      navigateInApp(`${parsed.pathname}${parsed.search}${parsed.hash}`);
      return;
    }
    void openInAppBrowser(parsed.href);
    return;
  }

  window.location.assign(parsed.href);
}
